import React, { createContext, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IOfficeCreationDTO } from '@common-frontend-submodule/interfaces/office/ioffice-creation.interface';
import { IOfficeUpdateDTO } from '@common-frontend-submodule/interfaces/office/ioffice-update.interface';
import { IGetOfficesRequestDTO } from '@common-frontend-submodule/interfaces/office/iget-offices-request.interface';
import { IOfficeDTO } from '@common-frontend-submodule/interfaces/office/ioffice.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { OfficeRoutes } from '@common-frontend-submodule/enums/routes/office-routes.enum';
import { OfficeAccountRoutes } from '@common-frontend-submodule/enums/routes/office-account-routes.enum'
import { IOfficeAccountDTO } from '@common-frontend-submodule/interfaces/office-account/ioffice-account.interface'
import { IOfficeAccountCreationDTO } from '@common-frontend-submodule/interfaces/office-account/ioffice-account-creation.interface';

const apiClient = ApiClient.getInstance();

interface OfficeContextProps {
  getById: (officeId: string) => Promise<IApiResponseGenericDTO<IOfficeDTO>>;
  allOfficeAccounts: () => Promise<IApiResponseGenericDTO<IOfficeAccountDTO[]>>;
  filter: (model: IGetOfficesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IOfficeDTO>>>;
  getOffices: () => Promise<IApiResponseGenericDTO<IOfficeDTO[]>>;
  create: (model: IOfficeCreationDTO) => Promise<IApiResponseGenericDTO<IOfficeDTO>>;
  getOfficeAccounts: (officeId: string) => Promise<IApiResponseGenericDTO<IOfficeAccountDTO>>;
  officeAccount: (model: IOfficeAccountCreationDTO) => Promise<IApiResponseGenericDTO<boolean>>;
  update: (officeId: string, model: IOfficeUpdateDTO) => Promise<IApiResponseGenericDTO<IOfficeDTO>>;
  delete: (officeId: string) => Promise<IApiResponseGenericDTO<boolean>>;
  deleteLinked: (linkedOfficeId: string) => Promise<IApiResponseGenericDTO<boolean>>;
  deleteOfficeAccount: (id: string) => Promise<IApiResponseGenericDTO<boolean>>
}

const OfficeContext = createContext<OfficeContextProps | undefined>(undefined);

export const OfficeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getById = async (officeId: string) => {
    return await apiClient.getRequest<IOfficeDTO>(`${OfficeRoutes.BasePrefix}/${officeId}`);
  };

  const allOfficeAccounts = async () => {
    return await apiClient.getRequest<IOfficeAccountDTO[]>(`${OfficeAccountRoutes.BasePrefix}/${OfficeRoutes.All}`);
  };

  const filter = async (model: IGetOfficesRequestDTO) => {
    return await apiClient.postRequest<IGetOfficesRequestDTO, IPaginatedResultDTO<IOfficeDTO>>(`${OfficeRoutes.BasePrefix}/${OfficeRoutes.List}`, model);
  };

  const create = async (model: IOfficeCreationDTO) => {
    return await apiClient.postRequest<IOfficeCreationDTO, IOfficeDTO>(OfficeRoutes.BasePrefix, model);
  };

  const getOffices = async() => {
    return await apiClient.getRequest<IOfficeDTO[]>(`${OfficeRoutes.BasePrefix}/${OfficeRoutes.All}`)
  }

  const getOfficeAccounts = async (officeId: string) => {
    return await apiClient.getRequest<IOfficeAccountDTO>(`${OfficeRoutes.BasePrefix}/linked/${officeId}`);
  };

  const officeAccount = async (model: IOfficeAccountCreationDTO) => {
    return await apiClient.postRequest<IOfficeAccountCreationDTO, boolean>(`${OfficeAccountRoutes.BasePrefix}`, model);
  };

  const update = async (officeId: string, model: IOfficeUpdateDTO) => {
    return await apiClient.putRequest<IOfficeUpdateDTO, IOfficeDTO>(`${OfficeRoutes.BasePrefix}/${officeId}`, model);
  };

  const deleteOffice = async (officeId: string) => {
    return await apiClient.deleteRequest<null, boolean>(`${OfficeRoutes.BasePrefix}/${officeId}`);
  };

  const deleteOfficeAccount = async (id: string) => {
    return await apiClient.deleteRequest<null, boolean>(`${OfficeAccountRoutes.BasePrefix}/${id}`)
  }

  const deleteLinked = async (linkedOfficeId: string) => {
    return await apiClient.deleteRequest<null, boolean>(`${OfficeRoutes.BasePrefix}/linked/${linkedOfficeId}`);
  };

  return (
    <OfficeContext.Provider value={{ getById, allOfficeAccounts, filter, create, getOfficeAccounts, officeAccount, update, delete: deleteOffice, deleteLinked, getOffices, deleteOfficeAccount }}>
      {children}
    </OfficeContext.Provider>
  );
};

export const useOfficeStore = () => {
  const context = useContext(OfficeContext);
  if (!context) {
    throw new Error('useOfficeStore must be used within an OfficeProvider');
  }
  return context;
};
